import { useUploadFile } from '@core/shared';
import { OptionsApi, WebinarDataWithAssets } from '@core/swagger-client';
import { Button, Delete, ErrorMessage, ImageAltText, Upload, useFileDropzone } from '@core/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
    webinar: WebinarDataWithAssets;
}

export const EditProfileImage = ({ webinar }: Props) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { upload, progress, isSuccess } = useUploadFile(`/api/webinar/${webinar.webinarId}/profile-image`);
    const { getRootProps, getInputProps, isDragActive, open, rejectionCode } = useFileDropzone({
        multiple: false,
        onDrop: (acceptedFiles) => {
            if (acceptedFiles.length === 0) {
                return;
            }

            const filePayload = new FormData();
            filePayload.append('file', acceptedFiles[0]);
            upload(filePayload).then(() => {
                queryClient.invalidateQueries({ queryKey: ['webinar'] });
                queryClient.invalidateQueries({ queryKey: ['getContentSummary'] });
            });
        },
        accept: {
            'image/png': ['.png'],
            'image/jpg': ['.jpg'],
            'image/webp': ['.webp']
        }
    });

    const altTextMutation = useMutation(OptionsApi.webinar.updateProfileImageAltText());
    const refreshWebinar = () => {
        queryClient.invalidateQueries({ queryKey: ['webinar'] });
        queryClient.invalidateQueries({ queryKey: ['getContentSummary'] });
    };
    const deleteProfileImageMutation = useMutation(OptionsApi.webinar.deleteProfileImage());
    const deleteImage = (e: MouseEvent) => {
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();

        deleteProfileImageMutation.mutate(webinar.webinarId, {
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['webinar'] });
                queryClient.invalidateQueries({ queryKey: ['getContentSummary'] });
            }
        });
    };

    return (
        <>
            <p className="text-sm mb-3 opacity-80">{t('Content.Overview.Images.Label')}</p>
            <div className="relative pt-4 menu:pt-0 z-10">
                <div className="relative flex items-start -mb-4 ps-4 md:ps-10">
                    <div
                        {...getRootProps()}
                        className={`group relative flex-none size-20 menu:size-24 md:size-30 ${isDragActive ? 'bg-accent-lighter' : 'bg-account '} hover:bg-accent-lighter focus-visible:bg-accent-lighter rounded-lg flex items-center justify-center text-white text-3xl md:text-5xl uppercase font-light outline outline-2 outline-white overflow-hidden transition-colors duration-150`}
                    >
                        {webinar.assets.profileImage && <img src={webinar.assets.profileImage.imageUrl} alt="" />}
                        <div
                            className={`absolute inset-0 rounded-lg border-2 border-dashed ${!webinar.assets.profileImage && !isDragActive && 'border-white'} ${webinar.assets.profileImage && !isDragActive && 'border-transparent'} ${isDragActive && 'border-accent'} group-hover:border-accent group-focus-visible:border-accent transition-colors duration-150`}
                        ></div>
                    </div>
                    <div className="flex flex-wrap gap-4 ms-4">
                        <Button variant="primary-outline" onClick={open} aria-label={t('WebinarDetails.Button.UploadProfileImage.AriaLabel')}>
                            <Upload classNames="size-6 flex-none stroke-current menu:me-2" />
                            <span className="hidden menu:inline">{t('Generic.Button.UploadImage')}</span>
                        </Button>
                        {/* Only for its own image: the default image's alt text is set on the account */}
                        {webinar.hasOwnProfileImage && (
                            <ImageAltText
                                altText={webinar.assets.profileImage?.altText}
                                onSave={(altText) => altTextMutation.mutateAsync({ webinarId: webinar.webinarId, altText }).then(refreshWebinar)}
                            />
                        )}
                        {/* Only its own image can be removed (back to the default); Assets falls back to the default */}
                        {webinar.hasOwnProfileImage && (
                            <Button
                                onClick={deleteImage}
                                isLoading={deleteProfileImageMutation.isPending}
                                variant="warning-outline"
                                aria-label={t('WebinarDetails.Button.UseDefaultProfileImage.AriaLabel')}
                            >
                                <Delete classNames="size-6 flex-none stroke-current menu:me-2" />
                                <span className="hidden menu:inline">{t('WebinarDetails.Button.UseDefaultImage')}</span>
                            </Button>
                        )}
                    </div>
                    <input {...getInputProps()} />
                </div>
                {rejectionCode && <ErrorMessage errorSubject="Generic.FileRejection." errorCode={rejectionCode} classNames="mt-4" />}
            </div>
        </>
    );
};
